import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RemindPreviewModal } from "@/components/dashboard/RemindPreviewModal";
import { RecordPaymentDialog } from "@/components/dashboard/RecordPaymentDialog";
import { ChevronLeft, FolderOpen, FileText, ClipboardList, Camera, Trash2, Link2, Link2Off, Search, Mail } from "lucide-react";
import { OpenInBrowserButton } from "@/components/dashboard/browser/OpenInBrowser";
import PhotoCropDialog from "@/components/dashboard/PhotoCropDialog";

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const initialsOf = (name = "") => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

// Match the pill logic on the Projects list / detail — legacy statuses like
// "In Progress" / "Completed" / "On Hold" all collapse to Active/Inactive.
const isActive = (s) => {
  if (!s) return true;
  return !(s === "Inactive" || s === "Completed" || s === "On Hold");
};

// Collapse-to-3 helper row for the detail lists
const ShowMoreRow = ({ expanded, onToggle, hidden, testid }) => (
  <li>
    <button
      onClick={onToggle}
      data-testid={testid}
      className="w-full px-4 py-2 text-center font-mono text-[10px] font-semibold uppercase tracking-widest text-[#2c6eeb] transition-colors hover:bg-blue-50/60"
    >
      {expanded ? "Show less" : `Show more (${hidden} more)`}
    </button>
  </li>
);

const contractValueOf = (p) => {
  const lines = p.contract_lines || [];
  return lines.reduce((sum, l) => sum + (Number(l.contract_value) || 0), 0);
};

// Inline text field — click to edit, blur or Enter to save.
const EditableField = ({ label, value, onSave, placeholder = "Add…", multiline = false, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => setDraft(value || ""), [value]);

  const commit = async () => {
    setEditing(false);
    if ((draft || "") === (value || "")) return;
    try {
      await onSave(draft);
    } catch {
      toast.error(`Could not save ${label.toLowerCase()}`);
      setDraft(value || "");
    }
  };

  return (
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      {editing ? (
        multiline ? (
          <textarea
            autoFocus
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            data-testid={testid}
            className="mt-0.5 w-full resize-y rounded-md border border-[#2c6eeb] px-2 py-1 text-sm text-gray-800 focus:outline-none"
          />
        ) : (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && commit()}
            data-testid={testid}
            className="mt-0.5 w-full rounded-md border border-[#2c6eeb] px-2 py-1 text-sm text-gray-800 focus:outline-none"
          />
        )
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={testid}
          className="mt-0.5 block w-full whitespace-pre-wrap break-words rounded px-1 py-0.5 text-left text-sm hover:bg-gray-50"
        >
          {value ? (
            <span className="text-gray-700">{value}</span>
          ) : (
            <span className="text-[#2c6eeb] hover:underline">{placeholder}</span>
          )}
        </button>
      )}
    </div>
  );
};

const CustomerDetail = ({ customerId, customers, onBack, onSelect, onUpdated, onOpenProject }) => {
  const [customer, setCustomer] = useState(
    () => customers.find((c) => c.customer_id === customerId) || null
  );
  const [projects, setProjects] = useState([]);
  const [receivables, setReceivables] = useState(null); // null=loading
  // Detail lists collapse to the first 3 rows; show more/less expands them
  const [projectsExpanded, setProjectsExpanded] = useState(false);
  const [receivablesExpanded, setReceivablesExpanded] = useState(false);
  const [qboInvExpanded, setQboInvExpanded] = useState(false);
  const [qboEstExpanded, setQboEstExpanded] = useState(false);
  const [shipSame, setShipSame] = useState(true);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);

  // QuickBooks integration state
  const [qboInvoices, setQboInvoices] = useState(null); // null=loading, [] or [{...}]
  const [qboEstimates, setQboEstimates] = useState(null);
  const [qboLinked, setQboLinked] = useState(false);
  const [qboPickerOpen, setQboPickerOpen] = useState(false);
  const [qboNotConnected, setQboNotConnected] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get(`/customers/${customerId}`).then((r) => {
      if (!cancel) setCustomer(r.data);
    }).catch(() => {});
    api.get(`/projects`).then((r) => {
      if (!cancel) setProjects(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    setReceivables(null);
    api.get(`/customers/${customerId}/receivables`).then((r) => {
      if (!cancel) setReceivables(r.data);
    }).catch(() => { if (!cancel) setReceivables({ rows: [], total: 0 }); });
    return () => { cancel = true; };
  }, [customerId]);

  // Reload QBO data whenever the customer's qbo linkage changes.
  useEffect(() => {
    if (!customer) return;
    let cancel = false;
    setQboInvoices(null);
    setQboEstimates(null);
    setQboNotConnected(false);
    Promise.all([
      api.get(`/customers/${customerId}/qbo/invoices`),
      api.get(`/customers/${customerId}/qbo/estimates`),
    ])
      .then(([inv, est]) => {
        if (cancel) return;
        setQboLinked(!!inv.data?.linked);
        setQboInvoices(inv.data?.invoices || []);
        setQboEstimates(est.data?.estimates || []);
      })
      .catch((err) => {
        if (cancel) return;
        // 400 = not connected; render CTA. Anything else — leave empty.
        if (err?.response?.status === 400) setQboNotConnected(true);
        setQboInvoices([]);
        setQboEstimates([]);
      });
    return () => { cancel = true; };
  }, [customerId, customer?.qbo_customer_id]);

  useEffect(() => {
    if (!customer) return;
    // If the stored shipping address differs from the billing address then
    // start in "custom shipping" mode so the field is visible.
    const billing = [customer.street_address, [customer.city, customer.province].filter(Boolean).join(", "), customer.zip].filter(Boolean).join(", ");
    const ship = (customer.shipping_address || "").trim();
    setShipSame(!ship || ship === billing);
  }, [customer]);

  const save = async (patch) => {
    const r = await api.put(`/customers/${customerId}`, { ...customer, ...patch });
    setCustomer(r.data);
    onUpdated?.();
  };

  // Reminder preview window (confirm before sending) from the Unpaid invoices card.
  const [remindOpen, setRemindOpen] = useState(false);
  const [payFor, setPayFor] = useState(null); // invoice row in the Record-payment dialog
  const reloadReceivables = () => {
    setReceivables(null);
    api.get(`/customers/${customerId}/receivables`).then((r2) => setReceivables(r2.data)).catch(() => {});
  };

  // Read a File into a data URL and open the crop dialog.
  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCropSrc(String(r.result));
    r.readAsDataURL(f);
  };

  const savePhoto = async (_file, dataUrl) => {
    try {
      const r = await api.post(`/customers/${customerId}/photo`, { data_url: dataUrl });
      setCustomer(r.data);
      onUpdated?.();
      toast.success("Photo updated");
    } catch {
      toast.error("Could not save photo");
    } finally {
      setCropSrc(null);
    }
  };

  const removePhoto = async () => {
    try {
      const r = await api.delete(`/customers/${customerId}/photo`);
      setCustomer(r.data);
      onUpdated?.();
    } catch {
      toast.error("Could not remove photo");
    }
  };

  const linkQboCustomer = async (qboCust) => {
    try {
      await api.post(`/customers/${customerId}/qbo/link`, {
        qbo_customer_id: qboCust.id,
        qbo_customer_display_name: qboCust.display_name,
      });
      setCustomer((c) => c ? { ...c, qbo_customer_id: qboCust.id, qbo_customer_display_name: qboCust.display_name } : c);
      setQboPickerOpen(false);
      toast.success(`Linked to ${qboCust.display_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link");
    }
  };

  const unlinkQboCustomer = async () => {
    if (!window.confirm("Unlink this customer from QuickBooks? Invoices/Estimates will no longer show here.")) return;
    try {
      await api.post(`/customers/${customerId}/qbo/link`, {
        qbo_customer_id: null,
        qbo_customer_display_name: null,
      });
      setCustomer((c) => c ? { ...c, qbo_customer_id: null, qbo_customer_display_name: null } : c);
      toast.success("Unlinked from QuickBooks");
    } catch {
      toast.error("Could not unlink");
    }
  };

  const customerProjects = useMemo(() => {
    if (!customer) return [];
    const target = (customer.name || "").trim().toLowerCase();
    return projects
      .filter((p) => (p.customer || "").trim().toLowerCase() === target)
      .sort((a, b) => Number(isActive(b.status)) - Number(isActive(a.status)) || (a.name || "").localeCompare(b.name || ""));
  }, [projects, customer]);

  if (!customer) {
    return (
      <div className="bg-[#f4f5f8] p-6 text-center text-sm text-gray-500">
        Loading customer…
      </div>
    );
  }

  const billing = [customer.street_address, [customer.city, customer.province].filter(Boolean).join(", "), customer.zip]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="min-h-[80vh] bg-[#f4f5f8] text-gray-800" data-testid="customer-detail">
      {/* header — back only */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <button
          onClick={onBack}
          data-testid="back-to-customers"
          className="flex items-center gap-1 text-sm font-semibold text-[#2c6eeb] hover:underline"
        >
          <ChevronLeft className="h-4 w-4" /> Customers
        </button>
      </div>

      <div className="space-y-4 p-4">
        {/* summary card */}
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1.6fr_1fr]">
            {/* identity */}
            <div className="flex gap-4">
              <div className="group relative h-16 w-16 shrink-0">
                {customer.photo ? (
                  <img
                    src={customer.photo}
                    alt={customer.name}
                    className="h-16 w-16 rounded-full object-cover"
                    data-testid="customer-photo"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-lg font-semibold text-gray-500">
                    {initialsOf(customer.name)}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="customer-photo-upload"
                  aria-label="Change photo"
                  title="Change photo"
                  className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-[#2c6eeb] text-white shadow-sm hover:bg-[#1f5bd0]"
                >
                  <Camera className="h-3.5 w-3.5" />
                </button>
                {customer.photo && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    data-testid="customer-photo-remove"
                    aria-label="Remove photo"
                    title="Remove photo"
                    className="absolute -top-1 -right-1 hidden h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-red-500 text-white shadow-sm hover:bg-red-600 group-hover:flex"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onFilePicked}
                  className="hidden"
                  data-testid="customer-photo-input"
                />
              </div>
              <div>
                <h2 className="flex items-center gap-2 text-xl font-semibold leading-tight text-gray-800" data-testid="detail-customer-name">
                  {customer.name}
                  <OpenInBrowserButton url={customer.website} name={customer.name} testid="detail-customer-website" />
                </h2>
                <p className="mt-1 text-sm text-gray-500">{customer.company_name || ""}</p>
              </div>
            </div>

            {/* contact + address */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <EditableField label="Email" value={customer.email} placeholder="Add email" testid="detail-email" onSave={(v) => save({ email: v })} />
              <EditableField label="Phone" value={customer.phone} placeholder="Add phone number" testid="detail-phone" onSave={(v) => save({ phone: v })} />
              <EditableField label="Cell phone" value={customer.cell_phone} placeholder="Add cell number" testid="detail-cell-phone" onSave={(v) => save({ cell_phone: v })} />
              <EditableField label="Website" value={customer.website} placeholder="Add website" testid="detail-website" onSave={(v) => save({ website: v })} />
              <div className="col-span-2">
                <EditableField label="Billing address" value={billing} placeholder="Add billing address" testid="detail-billing" multiline onSave={(v) => {
                  // Store the entire address string on street_address so we
                  // keep the UX simple; the granular fields stay empty.
                  return save({ street_address: v, city: "", province: "", zip: "" });
                }} />
              </div>
              <div className="col-span-2">
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs text-gray-400">Shipping address</p>
                  <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-500">
                    <input
                      type="checkbox"
                      checked={shipSame}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setShipSame(on);
                        if (on) save({ shipping_address: "" });
                      }}
                      data-testid="ship-same-toggle"
                      className="h-3.5 w-3.5 rounded border-gray-300 text-[#2c6eeb]"
                    />
                    Same as billing
                  </label>
                </div>
                {shipSame ? (
                  <p className="rounded px-1 py-0.5 text-sm text-gray-500">{billing || "—"}</p>
                ) : (
                  <EditableField label="" value={customer.shipping_address} placeholder="Add shipping address" testid="detail-shipping" multiline onSave={(v) => save({ shipping_address: v })} />
                )}
              </div>
              <div className="col-span-2">
                <EditableField label="Notes" value={customer.notes} placeholder="Add notes" testid="detail-notes" multiline onSave={(v) => save({ notes: v })} />
              </div>
            </div>

            {/* financial summary */}
            <div className="rounded-lg bg-[#eaf2fb] p-4">
              <p className="mb-3 text-sm font-semibold text-gray-700">Financial summary</p>
              <div className="mb-3">
                <span className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="h-2 w-2 rounded-full bg-amber-400" /> Open balance
                </span>
                <p className="text-2xl font-semibold text-gray-800" data-testid="detail-open-balance">
                  {money(customer.open_balance)}
                </p>
              </div>
              <div>
                <span className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="h-2 w-2 rounded-full bg-red-500" /> Overdue payment
                </span>
                <p className="text-2xl font-semibold text-gray-800">{money(customer.overdue_balance)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Projects list */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-[#2c6eeb]" />
              <h3 className="text-sm font-semibold text-gray-800">Projects</h3>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400" data-testid="projects-count">
              {customerProjects.length} total
            </span>
          </div>
          {customerProjects.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">No projects linked to this customer yet.</p>
          ) : (
            <ul>
              {(projectsExpanded ? customerProjects : customerProjects.slice(0, 3)).map((p) => {
                const active = isActive(p.status);
                return (
                  <li key={p.project_id} className="border-b border-gray-100 last:border-b-0">
                    <button
                      onClick={() => onOpenProject?.(p.project_id)}
                      data-testid={`customer-project-${p.project_id}`}
                      className={`group flex w-full items-center gap-3 border-l-4 px-4 py-2.5 text-left transition-colors hover:bg-gray-50 ${
                        active ? "border-emerald-400 bg-emerald-50/40" : "border-red-400 bg-red-50/40"
                      }`}
                    >
                      <span className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest ${
                        active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`}>
                        {active ? "Active" : "Inactive"}
                      </span>
                      <span className="flex min-w-0 flex-1 items-baseline justify-between gap-3">
                        <span className="truncate text-sm font-medium text-gray-800 group-hover:text-[#2c6eeb] group-hover:underline">{p.name}</span>
                        <span className="shrink-0 font-mono text-xs font-semibold text-gray-700">
                          {money(contractValueOf(p))}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {customerProjects.length > 3 && (
                <ShowMoreRow
                  expanded={projectsExpanded}
                  onToggle={() => setProjectsExpanded((v) => !v)}
                  hidden={customerProjects.length - 3}
                  testid="projects-show-more"
                />
              )}
            </ul>
          )}
        </div>

        {/* -------------------- QuickBooks linkage -------------------- */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-800">QuickBooks</h3>
            </div>
            {customer.qbo_customer_id ? (
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-700">
                  Linked · {customer.qbo_customer_display_name || `QBO ${customer.qbo_customer_id}`}
                </span>
                <button
                  onClick={unlinkQboCustomer}
                  data-testid="qbo-unlink-btn"
                  className="rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-500 hover:border-red-300 hover:text-red-600"
                >
                  <Link2Off className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setQboPickerOpen(true)}
                data-testid="qbo-link-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
              >
                <Search className="h-3.5 w-3.5" /> Link to QuickBooks customer
              </button>
            )}
          </div>
          {qboNotConnected && (
            <p className="px-4 py-4 text-center text-xs text-amber-700">
              QuickBooks is not connected. Ask a superadmin to connect it in <span className="font-semibold">Settings → API</span>.
            </p>
          )}
        </div>

        {/* Unpaid invoices (from the synced QuickBooks cache — no live QBO call needed) */}
        <div className="rounded-lg border border-gray-200 bg-white" data-testid="receivables-box">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-gray-800">Unpaid invoices</h3>
            </div>
            {receivables && receivables.rows.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-amber-600" data-testid="receivables-total">
                  {money(receivables.total)} due
                </span>
                <button
                  onClick={() => setRemindOpen(true)}
                  data-testid="receivables-remind-btn"
                  title="Preview and send an overdue reminder"
                  className="inline-flex items-center gap-1 rounded bg-[#1a3fd1] px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-[#1633b8]"
                >
                  <Mail className="h-3 w-3" /> Remind
                </button>
              </div>
            )}
          </div>
          {receivables === null ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">Loading…</p>
          ) : receivables.rows.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400" data-testid="receivables-empty">Nothing outstanding — every synced invoice is paid.</p>
          ) : (
            <ul data-testid="receivables-list">
              {(receivablesExpanded ? receivables.rows : receivables.rows.slice(0, 3)).map((r) => (
                <li key={r.qbo_id} className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 text-sm last:border-b-0" data-testid={`receivable-${r.qbo_id}`}>
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] font-semibold text-gray-700">#{r.doc_number}</p>
                    <p className="text-[11px] text-gray-400">
                      {r.txn_date || ""}
                      {r.due_date && <span className={r.overdue ? "font-semibold text-red-600" : ""}> · due {r.due_date}</span>}
                      {r.last_reminder_at && <span> · reminded {r.last_reminder_at}</span>}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <p className={`font-mono text-sm font-semibold ${r.overdue ? "text-red-600" : "text-amber-700"}`}>{money(r.balance)}</p>
                    <button
                      onClick={() => setPayFor(r)}
                      data-testid={`mark-paid-${r.qbo_id}`}
                      title="Record a full or partial payment in QuickBooks"
                      className="whitespace-nowrap rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-700 transition-colors hover:bg-emerald-100"
                    >
                      Mark paid
                    </button>
                  </div>
                </li>
              ))}
              {receivables.rows.length > 3 && (
                <ShowMoreRow
                  expanded={receivablesExpanded}
                  onToggle={() => setReceivablesExpanded((v) => !v)}
                  hidden={receivables.rows.length - 3}
                  testid="receivables-show-more"
                />
              )}
            </ul>
          )}
        </div>

        {/* Invoices list (live from QBO) */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#2c6eeb]" />
              <h3 className="text-sm font-semibold text-gray-800">Invoices</h3>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
              {qboInvoices ? `${qboInvoices.length} total` : "…"}
            </span>
          </div>
          {qboInvoices === null ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">Loading…</p>
          ) : !qboLinked ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">
              Link a QuickBooks customer above to pull invoices here.
            </p>
          ) : qboInvoices.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">No invoices in QuickBooks for this customer.</p>
          ) : (
            <ul data-testid="qbo-invoices-list">
              {(qboInvExpanded ? qboInvoices : qboInvoices.slice(0, 3)).map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 text-sm last:border-b-0">
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] font-semibold text-gray-700">#{t.doc_number || t.id}</p>
                    <p className="text-[11px] text-gray-400">{t.txn_date || ""}{t.due_date ? ` · due ${t.due_date}` : ""}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-mono text-sm font-semibold text-gray-800">{money(t.total)}</p>
                    {Number(t.balance) > 0 ? (
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-amber-600">
                        {money(t.balance)} due
                      </p>
                    ) : (
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Paid</p>
                    )}
                  </div>
                </li>
              ))}
              {qboInvoices.length > 3 && (
                <ShowMoreRow
                  expanded={qboInvExpanded}
                  onToggle={() => setQboInvExpanded((v) => !v)}
                  hidden={qboInvoices.length - 3}
                  testid="qbo-invoices-show-more"
                />
              )}
            </ul>
          )}
        </div>

        {/* Estimates list (live from QBO) */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-[#2c6eeb]" />
              <h3 className="text-sm font-semibold text-gray-800">Estimates</h3>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
              {qboEstimates ? `${qboEstimates.length} total` : "…"}
            </span>
          </div>
          {qboEstimates === null ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">Loading…</p>
          ) : !qboLinked ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">
              Link a QuickBooks customer above to pull estimates here.
            </p>
          ) : qboEstimates.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">No estimates in QuickBooks for this customer.</p>
          ) : (
            <ul data-testid="qbo-estimates-list">
              {(qboEstExpanded ? qboEstimates : qboEstimates.slice(0, 3)).map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 text-sm last:border-b-0">
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] font-semibold text-gray-700">#{t.doc_number || t.id}</p>
                    <p className="text-[11px] text-gray-400">
                      {t.txn_date || ""}
                      {t.expiration_date ? ` · exp ${t.expiration_date}` : ""}
                      {t.status ? ` · ${t.status}` : ""}
                    </p>
                  </div>
                  <p className="shrink-0 font-mono text-sm font-semibold text-gray-800">{money(t.total)}</p>
                </li>
              ))}
              {qboEstimates.length > 3 && (
                <ShowMoreRow
                  expanded={qboEstExpanded}
                  onToggle={() => setQboEstExpanded((v) => !v)}
                  hidden={qboEstimates.length - 3}
                  testid="qbo-estimates-show-more"
                />
              )}
            </ul>
          )}
        </div>
      </div>

      {remindOpen && customer && (
        <RemindPreviewModal
          customer={receivables?.customer_name || customer.name}
          onClose={() => setRemindOpen(false)}
          onSent={() => { setRemindOpen(false); reloadReceivables(); }}
        />
      )}

      {payFor && (
        <RecordPaymentDialog
          invoice={payFor}
          onClose={() => setPayFor(null)}
          onRecorded={() => { setPayFor(null); reloadReceivables(); }}
        />
      )}

      <PhotoCropDialog
        src={cropSrc}
        open={!!cropSrc}
        onCancel={() => setCropSrc(null)}
        onConfirm={savePhoto}
      />

      {qboPickerOpen && (
        <QboCustomerPicker
          onClose={() => setQboPickerOpen(false)}
          onPick={linkQboCustomer}
          initialQuery={customer.name || ""}
        />
      )}
    </div>
  );
};

const QboCustomerPicker = ({ onClose, onPick, initialQuery }) => {
  const [query, setQuery] = useState(initialQuery || "");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const runSearch = async (q) => {
    setBusy(true);
    setErr("");
    try {
      const r = await api.get("/qbo/customers/search", { params: { q: q || "" } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "QuickBooks search failed. Is it connected?");
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { runSearch(initialQuery || ""); }, [initialQuery]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3"
      onClick={onClose}
      data-testid="qbo-picker-dialog"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-600">Link to QuickBooks</p>
            <h3 className="text-lg font-bold uppercase tracking-tight text-gray-800">Pick QBO customer</h3>
          </div>
          <button onClick={onClose} className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-500 hover:text-gray-800">×</button>
        </div>
        <div className="border-b border-gray-100 p-3">
          <div className="flex gap-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch(query)}
              placeholder="Search by name…"
              data-testid="qbo-picker-search-input"
              className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-emerald-400"
            />
            <button
              onClick={() => runSearch(query)}
              disabled={busy}
              data-testid="qbo-picker-search-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-black hover:bg-emerald-400 disabled:opacity-50"
            >
              <Search className="h-3.5 w-3.5" /> Search
            </button>
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {err && <p className="p-4 text-center text-xs text-amber-700">{err}</p>}
          {!err && rows === null && <p className="p-6 text-center text-sm text-gray-400">Loading…</p>}
          {!err && rows && rows.length === 0 && (
            <p className="p-6 text-center text-sm text-gray-400">No matches. Try a different name.</p>
          )}
          {!err && rows && rows.length > 0 && (
            <ul>
              {rows.map((c) => (
                <li key={c.id} className="border-b border-gray-100 last:border-b-0">
                  <button
                    onClick={() => onPick(c)}
                    data-testid={`qbo-pick-${c.id}`}
                    className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-emerald-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-800">{c.display_name}</p>
                      <p className="truncate text-[11px] text-gray-400">
                        {c.primary_email || c.phone || `QBO ID ${c.id}`}
                      </p>
                    </div>
                    <span className="shrink-0 font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-600">
                      Link
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
